import { useInfiniteQuery } from "@tanstack/react-query";
import {
  type DocumentData,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  type QueryDocumentSnapshot,
  query,
  startAfter,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { queryKeys } from "../../../queries/queryKeys";
import { formatDate, formatearNumeros } from "../../../utils/formatters";
import {
  crearGastosRutaCollectionRef,
  type GastoRutaCompatible,
  obtenerHistorialGastosLegacyRecientes,
} from "../../../utils/rutaGastos";
import type { TablaGastoItem, TablaGastosProps, UseTablaGastosReturn } from "./types";

const GASTOS_POR_PAGINA = 20;

type GastoTabla = TablaGastoItem & {
  id: string;
};

type GastosPagina = {
  items: GastoTabla[];
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
};

const fetchGastosPagina = async (
  adminUid: string,
  rutaUid: string,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
): Promise<GastosPagina> => {
  const db = getFirestore();
  const gastosRef = crearGastosRutaCollectionRef(db, adminUid, rutaUid);
  const gastosQuery = cursor
    ? query(gastosRef, orderBy("fecha", "desc"), startAfter(cursor), limit(GASTOS_POR_PAGINA))
    : query(gastosRef, orderBy("fecha", "desc"), limit(GASTOS_POR_PAGINA));
  const gastosSnapshot = await getDocs(gastosQuery);
  const items = gastosSnapshot.docs.map((gastoDoc) => ({
    id: gastoDoc.id,
    ...(gastoDoc.data() as GastoRutaCompatible),
  }));

  return {
    items,
    nextCursor:
      gastosSnapshot.docs.length === GASTOS_POR_PAGINA
        ? (gastosSnapshot.docs[gastosSnapshot.docs.length - 1] ?? null)
        : null,
  };
};

export const useTablaGastos = ({ usuarioRuta }: TablaGastosProps): UseTablaGastosReturn => {
  const [legacyVisibleCount, setLegacyVisibleCount] = useState(GASTOS_POR_PAGINA);
  const adminUid = usuarioRuta?.adminUid;
  const rutaUid = usuarioRuta?.uid;
  const legacyGastos = useMemo(
    () =>
      obtenerHistorialGastosLegacyRecientes(usuarioRuta).map((gasto, index) => ({
        ...(gasto as GastoRutaCompatible),
        id: `legacy-${index}`,
      })),
    [usuarioRuta],
  );

  // Consulta ocasional (no se mira constantemente); staleTime moderado evita
  // repetir el fetch al reabrir esta pantalla para la misma ruta, y el gcTime
  // acotado evita acumular paginas sin techo en sesiones de 8+ horas.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    enabled: Boolean(adminUid && rutaUid),
    gcTime: 10 * 60 * 1000,
    getNextPageParam: (lastPage: GastosPagina) => lastPage.nextCursor,
    initialPageParam: null as QueryDocumentSnapshot<DocumentData> | null,
    queryFn: ({ pageParam }) => fetchGastosPagina(adminUid as string, rutaUid as string, pageParam),
    queryKey: queryKeys.gastos(adminUid, rutaUid),
    staleTime: 90 * 1000,
  });

  useEffect(() => {
    setLegacyVisibleCount(GASTOS_POR_PAGINA);
  }, [adminUid, rutaUid]);

  const gastos = useMemo(
    () => data?.pages.flatMap((pagina: GastosPagina) => pagina.items) ?? [],
    [data],
  );
  const cargaInicialFinalizada = !isLoading;
  const mostrarFallbackLegacy =
    cargaInicialFinalizada && gastos.length === 0 && legacyGastos.length > 0;
  const legacyGastosRender = useMemo(
    () => legacyGastos.slice(0, legacyVisibleCount),
    [legacyGastos, legacyVisibleCount],
  );
  const gastosRender = mostrarFallbackLegacy ? legacyGastosRender : gastos;
  const canLoadMore = mostrarFallbackLegacy
    ? legacyGastos.length > legacyVisibleCount
    : Boolean(hasNextPage);

  const handleLoadMore = useCallback(() => {
    if (mostrarFallbackLegacy) {
      setLegacyVisibleCount((previousVisibleCount) => previousVisibleCount + GASTOS_POR_PAGINA);
      return;
    }

    void fetchNextPage();
  }, [fetchNextPage, mostrarFallbackLegacy]);

  return {
    canLoadMore,
    formatDate,
    formatearNumeros,
    gastos: gastosRender,
    handleLoadMore,
    isLoading,
    isLoadingMore: isFetchingNextPage,
  };
};
